import React from 'react';
import { Mail, Phone, Linkedin, MapPin, ExternalLink, PenTool, Instagram } from 'lucide-react';

export const ContactFooter = () => {
  const handleLogoClick = () => {
    window.location.href = '/';
  };

  return (
    <footer className="bg-white border-t border-gray-200">
      <div className="container mx-auto px-4 md:px-6">
        <div className="max-w-7xl mx-auto">
          {/* Main Footer Content */}
          <div className="py-8 md:py-12">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">
              
              {/* Brand Section */}
              <div className="lg:col-span-1">
                <button 
                  onClick={handleLogoClick}
                  className="flex items-center hover:opacity-80 transition-opacity cursor-pointer mb-4"
                  aria-label="Go to home page"
                >
                  <img 
                    src="/A2ZAssist%20logo.png" 
                    alt="A2ZCampus Logo" 
                    className="w-12 h-12 md:w-16 md:h-16 mr-3 object-contain"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                    }}
                  />
                  <div className="flex items-center">
                    <span className="font-bold text-2xl md:text-3xl tracking-tight" style={{color: '#2C2420'}}>A2Z</span>
                    <span className="font-bold text-2xl md:text-3xl tracking-tight" style={{color: '#6B4423'}}>Campus</span>
                    <span className="text-lg font-bold ml-1 align-top" style={{color: '#6B4423'}}>™</span>
                  </div>
                </button>
                
                <p className="text-sm md:text-base mb-4 leading-relaxed" style={{color: '#6B4423'}}>
                  India's First ThinkTech™ Platform for clarity-first learning in AI, startups, and real-world skills.
                </p>
                
                <p className="text-xs md:text-sm italic" style={{color: '#2C2420'}}>
                  "Shaping Student Success – One Campus at a Time."
                </p>
              </div>

              {/* Contact Information */}
              <div className="lg:col-span-1">
                <h3 className="text-lg md:text-xl font-bold mb-4" style={{color: '#2C2420'}}>Get in Touch</h3>
                
                <div className="space-y-3">
                  <a 
                    href="mailto:info@a2zassist.com"
                    className="flex items-center text-sm md:text-base hover:opacity-80 transition-colors group"
                    style={{color: '#6B4423'}}
                  >
                    <Mail className="w-4 h-4 md:w-5 md:h-5 mr-3 flex-shrink-0" style={{color: '#C4A052'}} />
                    <span>info@a2zassist.com</span>
                    <ExternalLink className="w-3 h-3 ml-2 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </a>
                  
                  <div className="flex items-center text-sm md:text-base" style={{color: '#6B4423'}}>
                    <MapPin className="w-4 h-4 md:w-5 md:h-5 mr-3 flex-shrink-0" style={{color: '#C4A052'}} />
                    <span>Serving educational institutions across India</span>
                  </div>
                </div>
              </div>

              {/* Quick Links & Social */}
              <div className="lg:col-span-1">
                <h3 className="text-lg md:text-xl font-bold mb-4" style={{color: '#2C2420'}}>Quick Links</h3>
                
                <div className="space-y-3 mb-6">
                  <a 
                    href="/FAQ"
                    className="block text-sm md:text-base hover:opacity-80 transition-colors"
                    style={{color: '#6B4423'}}
                  >
                    Frequently Asked Questions
                  </a>
                  <a 
                    href="/privacy-policy"
                    className="block text-sm md:text-base hover:opacity-80 transition-colors"
                    style={{color: '#6B4423'}}
                  >
                    Privacy Policy
                  </a>
                  <a 
                    href="https://ReturnFounder.com" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center text-sm md:text-base hover:opacity-80 transition-colors group"
                    style={{color: '#6B4423'}}
                  >
                    <span>About Founder</span>
                    <ExternalLink className="w-3 h-3 ml-2 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </a>
                </div>

                <div>
                  <h4 className="text-base md:text-lg font-semibold mb-3" style={{color: '#2C2420'}}>Follow Us</h4>
                  <div className="flex space-x-3">
                    <a 
                      href="https://www.linkedin.com/showcase/a2zcampus" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="p-2 rounded-lg border border-gray-300 hover:border-gray-400 hover:bg-gray-50 transition-all"
                      aria-label="Follow A2ZCampus on LinkedIn"
                    >
                      <Linkedin className="w-4 h-4 md:w-5 md:h-5" style={{color: '#6B4423'}} />
                    </a>
                    <a 
                      href="https://medium.com/@A2ZASSIST"
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="p-2 rounded-lg border border-gray-300 hover:border-gray-400 hover:bg-gray-50 transition-all"
                      aria-label="Read A2Z Assist articles on Medium"
                    >
                      <PenTool className="w-4 h-4 md:w-5 md:h-5" style={{color: '#6B4423'}} />
                    </a>
                    <a 
                      href="https://www.instagram.com/a2zassist/"
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="p-2 rounded-lg border border-gray-300 hover:border-gray-400 hover:bg-gray-50 transition-all"
                      aria-label="Follow A2Z Assist on Instagram"
                    >
                      <Instagram className="w-4 h-4 md:w-5 md:h-5" style={{color: '#6B4423'}} />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="border-t border-gray-200 py-4 md:py-6">
            <div className="flex flex-col md:flex-row items-center justify-between space-y-2 md:space-y-0">
              <div className="text-xs md:text-sm" style={{color: '#6B4423'}}>
                © 2025 A2ZCampus™. All rights reserved.
              </div>
              
              <div className="flex items-center space-x-4 text-xs md:text-sm">
                <a 
                  href="/FAQ"
                  className="hover:underline transition-colors"
                  style={{color: '#6B4423'}}
                >
                  FAQ
                </a>
                <span style={{color: '#6B4423'}}>•</span>
                <a 
                  href="/privacy-policy"
                  className="hover:underline transition-colors"
                  style={{color: '#6B4423'}}
                >
                  Privacy Policy
                </a>
                <span style={{color: '#6B4423'}}>•</span>
                <a 
                  href="https://ReturnFounder.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="hover:underline transition-colors"
                  style={{color: '#6B4423'}}
                >
                  About Founder
                </a>
              </div>
            </div>
            
            {/* Powered by A2ZAssist */}
            <div className="text-center mt-3 md:mt-4 pt-3 md:pt-4 border-t border-gray-100">
              <p className="text-xs md:text-sm font-medium" style={{color: '#6B4423'}}>
                Powered by <span className="font-bold" style={{color: '#C4A052'}}>A2ZAssist™</span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};